'use client';

import {postGuideFeedback, type GuideFeedbackReason} from 'iq-interview_guides/feedback_api';
import {trackGuideFeedbackSubmitted} from 'iq-interview_guides/tracking';
import {X} from 'lucide-react';
import {Dialog} from 'radix-ui';
import {useState} from 'react';

const REASONS: {value: GuideFeedbackReason; label: string}[] = [
  {value: 'incorrect', label: 'Something is wrong'},
  {value: 'outdated', label: 'Out of date'},
  {value: 'missing_explanation', label: 'Missing info I needed'},
  {value: 'unclear', label: 'Hard to follow'},
  {value: 'typo', label: 'Typo or formatting'},
  {value: 'other', label: 'Other'},
];

interface GuideFeedbackDialogProps {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Reason box shown after a "No" vote. Closing without sending is fine: the vote is already saved.
export function GuideFeedbackDialog({slug, open, onOpenChange}: GuideFeedbackDialogProps) {
  const [reason, setReason] = useState<GuideFeedbackReason | ''>('');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const canSend = (reason !== '' || details.trim() !== '') && !sending;

  const send = async () => {
    if (!canSend) return;
    const finalReason = reason === '' ? 'other' : reason;
    setSending(true);
    setError('');
    try {
      await postGuideFeedback({slug, reason: finalReason, details: details.trim()});
      void trackGuideFeedbackSubmitted({slug, reason: finalReason});
      setSent(true);
      setTimeout(() => onOpenChange(false), 1500);
    } catch {
      setError('Could not send your feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 z-50 bg-black/40' />
        <Dialog.Content className='fixed left-1/2 top-1/2 z-50 w-[calc(100%-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl'>
          <Dialog.Description className='sr-only'>
            Tell us what was wrong with this guide.
          </Dialog.Description>
          <Dialog.Close
            aria-label='Close'
            className='absolute right-4 top-4 text-iq-grey-medium transition-colors hover:text-iq-grey-dark'>
            <X size={16} />
          </Dialog.Close>
          <DialogBody
            sent={sent}
            reason={reason}
            details={details}
            sending={sending}
            canSend={canSend}
            error={error}
            onReason={setReason}
            onDetails={setDetails}
            onSend={send}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface DialogBodyProps {
  sent: boolean;
  reason: GuideFeedbackReason | '';
  details: string;
  sending: boolean;
  canSend: boolean;
  error: string;
  onReason: (reason: GuideFeedbackReason | '') => void;
  onDetails: (details: string) => void;
  onSend: () => void;
}

function DialogBody(props: DialogBodyProps) {
  if (props.sent) {
    return (
      <Dialog.Title className='py-6 text-center text-base font-semibold text-iq-black'>
        Thanks. We&apos;ll use this when we refresh the guide.
      </Dialog.Title>
    );
  }
  return <FeedbackForm {...props} />;
}

function FeedbackForm({
  reason,
  details,
  sending,
  canSend,
  error,
  onReason,
  onDetails,
  onSend,
}: DialogBodyProps) {
  return (
    <>
      <Dialog.Title className='mb-4 pr-6 text-lg font-bold text-iq-black'>
        What was off?
      </Dialog.Title>
      <div className='mb-4 flex flex-wrap gap-2'>
        {REASONS.map((r) => (
          <ReasonChip
            key={r.value}
            label={r.label}
            selected={reason === r.value}
            onClick={() => onReason(reason === r.value ? '' : r.value)}
          />
        ))}
      </div>
      <textarea
        className='w-full resize-y rounded-lg border border-iq-grey-light p-3 text-sm text-iq-black outline-none focus:border-iq-orange'
        placeholder='Tell us more (optional)'
        aria-label='Details'
        maxLength={2000}
        rows={4}
        value={details}
        onChange={(e) => onDetails(e.target.value)}
      />
      {error && <p className='mt-2 text-sm text-iq-red'>{error}</p>}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          disabled={!canSend}
          onClick={onSend}
          className='rounded-lg bg-iq-orange px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-iq-orange-dark disabled:cursor-not-allowed disabled:opacity-50'>
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </>
  );
}

function ReasonChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  const tone = selected
    ? 'border-iq-orange bg-iq-orange-light text-iq-orange-dark'
    : 'border-iq-grey-light bg-white text-iq-grey-dark hover:border-iq-orange';
  return (
    <button
      type='button'
      aria-pressed={selected}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${tone}`}>
      {label}
    </button>
  );
}
